import { normalizeSyncBaseUrl } from "./syncSettings";
import { parseCareerSave, type CareerSaveBlob } from "./careerSave";
import { normalizeAccountEmail, validateAccountEmail, validateAccountPassword } from "./accountAuth";
import type { RemoteSnapshotListResult, RemoteSnapshotMeta } from "./syncApi";

export type AccountAuthResult =
  | { ok: true; email: string; accountId: string; token: string; hasCareer: boolean }
  | { ok: false; error: string; status?: number };

export type AccountCareerGetResult =
  | { ok: true; blob: CareerSaveBlob }
  | { ok: false; error: string; status?: number };

export type AccountCareerPutResult =
  | { ok: true; snapshotId?: string; retained?: number }
  | { ok: false; error: string; status?: number };

export type CareerSnapshotGetResult =
  | { ok: true; id: string; blob: CareerSaveBlob }
  | { ok: false; error: string; status?: number };

const FETCH_MS = 8000;

function joinUrl(baseUrl: string, path: string): string {
  return `${normalizeSyncBaseUrl(baseUrl)}${path}`;
}

async function fetchTimed(url: string, init: RequestInit, timeoutMs = FETCH_MS): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

function failMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    if (err.name === "AbortError") return "Sync server timed out.";
    return err.message || fallback;
  }
  return fallback;
}

export const UNEXPECTED_PAGE = "The server returned an unexpected page.";

function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** A 200 that is the app HTML (or any non-JSON body) is not an account result. */
async function readJsonObject(res: Response): Promise<Record<string, unknown> | null> {
  const type = res.headers?.get?.("content-type") ?? "";
  if (type.includes("text/html")) return null;
  try {
    const payload = await res.json();
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}

function authHeaders(token: string): HeadersInit {
  return {
    Accept: "application/json",
    Authorization: `Bearer ${token.trim()}`,
  };
}

async function postAuth(
  baseUrl: string,
  path: string,
  body: Record<string, string>,
): Promise<AccountAuthResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url) return { ok: false, error: "Enter a sync server base URL." };
  try {
    const res = await fetchTimed(joinUrl(url, path), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const payload = await readJsonObject(res);
    if (!payload) {
      return { ok: false, error: UNEXPECTED_PAGE, status: res.status };
    }
    if (res.status === 409) return { ok: false, error: textOf(payload.error) || "That email is already registered.", status: 409 };
    if (res.status === 401) return { ok: false, error: textOf(payload.error) || "Email or password does not match.", status: 401 };
    if (!res.ok || payload.ok !== true || !textOf(payload.token) || !textOf(payload.accountId)) {
      return {
        ok: false,
        error: textOf(payload.error) || (res.ok ? "Sign-in did not return an account." : "Sign-in failed."),
        status: res.status,
      };
    }
    return {
      ok: true,
      email: textOf(payload.email) || body.email,
      accountId: textOf(payload.accountId),
      token: textOf(payload.token),
      hasCareer: Boolean(payload.hasCareer),
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Account request failed.") };
  }
}

export async function registerNanodeAccount(
  baseUrl: string,
  input: { email: string; password: string },
): Promise<AccountAuthResult> {
  const email = validateAccountEmail(input.email);
  if (!email.ok) return email;
  const password = validateAccountPassword(input.password);
  if (!password.ok) return password;
  return postAuth(baseUrl, "/v1/auth/register", { email: email.email, password: password.password });
}

export async function loginNanodeAccount(
  baseUrl: string,
  input: { email: string; password: string },
): Promise<AccountAuthResult> {
  const email = validateAccountEmail(input.email);
  if (!email.ok) return email;
  if (!input.password) return { ok: false, error: "Enter a password." };
  return postAuth(baseUrl, "/v1/auth/login", {
    email: email.email,
    password: input.password,
  });
}

export async function resetNanodePassword(
  baseUrl: string,
  input: { email: string; resetToken: string; newPassword: string },
): Promise<{ ok: true } | { ok: false; error: string; status?: number }> {
  const email = validateAccountEmail(input.email);
  if (!email.ok) return email;
  const password = validateAccountPassword(input.newPassword);
  if (!password.ok) return password;
  if (!input.resetToken.trim()) return { ok: false, error: "Admin reset token is required." };
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url) return { ok: false, error: "Enter a sync server base URL." };
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/auth/reset"), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email.email,
        resetToken: input.resetToken.trim(),
        newPassword: password.password,
      }),
    });
    const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    if (!res.ok || !payload.ok) {
      return { ok: false, error: payload.error || `Reset HTTP ${res.status}`, status: res.status };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Password reset failed.") };
  }
}

export async function logoutNanodeAccount(
  baseUrl: string,
  token: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) return { ok: false, error: "Not signed into a Nanode account." };
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/auth/logout"), {
      method: "POST",
      headers: authHeaders(token),
    });
    if (!res.ok) return { ok: false, error: `Logout HTTP ${res.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Logout failed.") };
  }
}

export async function pullAccountCareer(baseUrl: string, token: string): Promise<AccountCareerGetResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) return { ok: false, error: "Nanode account session is required." };
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/account/career"), {
      method: "GET",
      headers: authHeaders(token),
    });
    if (res.status === 401) return { ok: false, error: "Account session expired. Sign in again.", status: 401 };
    if (res.status === 404) return { ok: false, error: "No career save on this account yet.", status: 404 };
    if (!res.ok) return { ok: false, error: `Career pull HTTP ${res.status}`, status: res.status };
    const parsed = parseCareerSave(await res.json().catch(() => null));
    if (!parsed.ok) return { ok: false, error: parsed.error };
    return { ok: true, blob: parsed.blob };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Career pull failed.") };
  }
}

export async function pushAccountCareer(
  baseUrl: string,
  token: string,
  blob: CareerSaveBlob,
): Promise<AccountCareerPutResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) return { ok: false, error: "Nanode account session is required." };
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/account/career"), {
      method: "PUT",
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(blob),
    });
    if (res.status === 401) return { ok: false, error: "Account session expired. Sign in again.", status: 401 };
    if (!res.ok) return { ok: false, error: `Career push HTTP ${res.status}`, status: res.status };
    const payload = (await res.json().catch(() => ({}))) as { snapshotId?: string; retained?: number };
    return {
      ok: true,
      snapshotId: typeof payload.snapshotId === "string" ? payload.snapshotId : undefined,
      retained: Number.isFinite(payload.retained) ? payload.retained : undefined,
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Career push failed.") };
  }
}

function parseCareerMetas(raw: unknown): RemoteSnapshotMeta[] {
  const rows = Array.isArray(raw) ? raw : [];
  const out: RemoteSnapshotMeta[] = [];
  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    if (typeof rec.id !== "string" || !rec.id.trim()) continue;
    out.push({
      id: rec.id,
      scope: "account",
      savedAtUtc: typeof rec.savedAtUtc === "string" ? rec.savedAtUtc : "",
      company: typeof rec.company === "string" && rec.company.trim() ? rec.company : "—",
      cashCad: Number.isFinite(rec.cashCad) ? Number(rec.cashCad) : 0,
      currency: typeof rec.currency === "string" && rec.currency.trim() ? rec.currency : "CAD",
      missionCount: Number.isFinite(rec.missionCount) ? Number(rec.missionCount) : 0,
      includeSecrets: false,
    });
  }
  return out;
}

/** Dated career copies owned by the signed-in Nanode account. */
export async function listCareerSnapshots(baseUrl: string, token: string): Promise<RemoteSnapshotListResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) return { ok: false, error: "Nanode account session is required." };
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/account/career/snapshots"), {
      method: "GET",
      headers: authHeaders(token),
    });
    if (res.status === 401) return { ok: false, error: "Account session expired. Sign in again.", status: 401 };
    if (res.status === 404) return { ok: false, error: "This sync server does not list career snapshots yet.", status: 404 };
    if (!res.ok) return { ok: false, error: `Career snapshots HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => ({}))) as { snapshots?: unknown; retention?: number };
    return {
      ok: true,
      snapshots: parseCareerMetas(body.snapshots),
      retention: Number.isFinite(body.retention) ? Number(body.retention) : 16,
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Career snapshot list failed.") };
  }
}

export async function getCareerSnapshot(
  baseUrl: string,
  token: string,
  id: string,
): Promise<CareerSnapshotGetResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim() || !id.trim()) {
    return { ok: false, error: "Nanode account session and snapshot id are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, `/v1/account/career/snapshots/${encodeURIComponent(id)}`), {
      method: "GET",
      headers: authHeaders(token),
    });
    if (res.status === 401) return { ok: false, error: "Account session expired. Sign in again.", status: 401 };
    if (res.status === 404) return { ok: false, error: "That career snapshot is gone.", status: 404 };
    if (!res.ok) return { ok: false, error: `Career snapshot HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => null)) as { ok?: boolean; id?: string; career?: unknown } | null;
    const parsed = parseCareerSave(body?.career ?? body);
    if (!parsed.ok) return { ok: false, error: parsed.error };
    return { ok: true, id: typeof body?.id === "string" ? body.id : id, blob: parsed.blob };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Career snapshot fetch failed.") };
  }
}

export { normalizeAccountEmail };
